/** @format */

import dayjs from 'dayjs'
import type { TraceAnalysisDetailSpan } from '../typescript'

export const LANGGRAPH_ROOT_KEY = '__ROOT__'

// --- Langfuse / LangGraph 属性键（OTEL + Langfuse 观测元数据）---

const APP_ROOT_ATTR = 'langfuse.internal.is_app_root'
const AS_ROOT_ATTR = 'langfuse.internal.as_root'
const LANGCHAIN_ROOT_ATTR = 'langfuse.observation.metadata.is_langchain_root'
const LS_INTEGRATION_ATTR = 'langfuse.observation.metadata.ls_integration'
const OBSERVATION_TYPE_ATTR = 'langfuse.observation.type'

const LANGGRAPH_STEP_ATTRS = ['langgraph_step', 'langfuse.observation.metadata.langgraph_step']
const LANGGRAPH_NODE_ATTRS = ['langgraph_node', 'langfuse.observation.metadata.langgraph_node']
const LANGGRAPH_PATH_ATTRS = ['langgraph_path', 'langfuse.observation.metadata.langgraph_path']
const LANGGRAPH_TRIGGERS_ATTRS = [
  'langgraph_triggers',
  'langfuse.observation.metadata.langgraph_triggers'
]
const TAGS_ATTRS = ['langfuse.observation.metadata.tags', 'tags']

/** Langfuse 拓扑节点角色，由 Attributes 推导 */
export type LangfuseSpanRole =
  | 'app_root'
  | 'framework_root'
  | 'no_step'
  | 'graph_step'
  | 'graph_nested'

export type LangGraphTriggerType = 'pregel_start' | 'branch' | 'join' | 'unknown'

export interface ParsedLangGraphTrigger {
  type: LangGraphTriggerType
  raw: string
  targetNode?: string
  predecessorNodes?: string[]
}

export interface LangGraphSpanEdge {
  from: string
  to: string
}

export interface LangGraphContext {
  spans: TraceAnalysisDetailSpan[]
  spanMap: Map<string, TraceAnalysisDetailSpan>
  spanIds: Set<string>
  stepGroups: Map<number, TraceAnalysisDetailSpan[]>
  sortedSteps: number[]
  minStep: number | null
}

// --- 属性读取 ---

function getAttribute(
  attrs: Record<string, string> | undefined,
  keys: string[]
): string | undefined {
  if (!attrs) return undefined
  for (const key of keys) {
    const value = attrs[key]
    if (value != null && value !== '') return value
  }
  return undefined
}

export function getLangGraphStep(attrs?: Record<string, string>): number | null {
  const raw = getAttribute(attrs, LANGGRAPH_STEP_ATTRS)
  if (raw == null || raw === '') return null
  const step = Number(raw)
  return Number.isFinite(step) ? step : null
}

export function getLangGraphNode(attrs?: Record<string, string>): string | null {
  return getAttribute(attrs, LANGGRAPH_NODE_ATTRS) ?? null
}

export function getLangGraphPath(attrs?: Record<string, string>): string[] {
  const raw = getAttribute(attrs, LANGGRAPH_PATH_ATTRS)
  if (!raw) return []
  const trimmed = raw.trim()
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) return parsed.map(item => String(item))
    } catch {
      return []
    }
  }
  return []
}

export function getObservationType(attrs?: Record<string, string>): string | null {
  return attrs?.[OBSERVATION_TYPE_ATTR] ?? null
}

export function getLsIntegration(attrs?: Record<string, string>): string | null {
  return attrs?.[LS_INTEGRATION_ATTR] ?? null
}

function parseJsonStringArray(raw: string): string[] {
  const trimmed = raw.trim()
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) return parsed.map(item => String(item))
    } catch {
      return []
    }
  }
  const tupleLike = trimmed.replace(/^[([]|[)\]]$/g, '')
  if (!tupleLike) return []
  return tupleLike
    .split(',')
    .map(item => item.trim().replace(/^['"]|['"]$/g, ''))
    .filter(Boolean)
}

export function getSpanTags(attrs?: Record<string, string>): string[] {
  const raw = getAttribute(attrs, TAGS_ATTRS)
  if (!raw) return []
  return parseJsonStringArray(raw)
}

/** tags 中的 seq:step:N，用于同节点内子 Span 排序 */
export function getSeqStep(attrs?: Record<string, string>): number | null {
  for (const tag of getSpanTags(attrs)) {
    const match = tag.match(/^seq:step:(\d+)$/)
    if (match) {
      const step = Number(match[1])
      if (Number.isFinite(step)) return step
    }
  }
  return null
}

/** tags 中的 graph:step:N */
export function getGraphStepTag(attrs?: Record<string, string>): number | null {
  for (const tag of getSpanTags(attrs)) {
    const match = tag.match(/^graph:step:(\d+)$/)
    if (match) {
      const step = Number(match[1])
      if (Number.isFinite(step)) return step
    }
  }
  return null
}

export function isAppRootSpan(span: TraceAnalysisDetailSpan): boolean {
  return span.Attributes?.[APP_ROOT_ATTR] === 'true'
}

export function isFrameworkRootSpan(span: TraceAnalysisDetailSpan): boolean {
  const attrs = span.Attributes
  return attrs?.[AS_ROOT_ATTR] === 'true' || attrs?.[LANGCHAIN_ROOT_ATTR] === 'true'
}

/** 根据 Langfuse / LangGraph Attributes 判定 Span 在拓扑中的角色 */
export function classifyLangfuseSpanRole(
  span: TraceAnalysisDetailSpan,
  ctx: LangGraphContext
): LangfuseSpanRole {
  if (isAppRootSpan(span)) return 'app_root'
  if (isFrameworkRootSpan(span)) return 'framework_root'

  const step = stepOf(span)
  if (step == null) return 'no_step'
  if (isStepLevelSpan(span, ctx)) return 'graph_step'
  return 'graph_nested'
}

export function parseLangGraphTriggerItem(raw: string): ParsedLangGraphTrigger {
  const trimmed = raw.trim()
  if (trimmed === '__pregel_start') {
    return { type: 'pregel_start', raw: trimmed }
  }
  const branchMatch = trimmed.match(/^branch:to:(.+)$/)
  if (branchMatch) {
    return { type: 'branch', raw: trimmed, targetNode: branchMatch[1] }
  }
  const joinMatch = trimmed.match(/^join:([^:]+):(.+)$/)
  if (joinMatch) {
    return {
      type: 'join',
      raw: trimmed,
      predecessorNodes: joinMatch[1]
        .split('+')
        .map(node => node.trim())
        .filter(Boolean),
      targetNode: joinMatch[2]
    }
  }
  return { type: 'unknown', raw: trimmed }
}

export function parseLangGraphTriggers(attrs?: Record<string, string>): string[] {
  const raw = getAttribute(attrs, LANGGRAPH_TRIGGERS_ATTRS)
  if (!raw) return []
  return parseJsonStringArray(raw)
}

export function parseLangGraphTriggerItems(
  attrs?: Record<string, string>
): ParsedLangGraphTrigger[] {
  return parseLangGraphTriggers(attrs).map(parseLangGraphTriggerItem)
}

// --- 时间与 Span 上下文 ---

const spanStartMs = (span: TraceAnalysisDetailSpan) => dayjs(span.StartTime).valueOf()

const spanEndMs = (span: TraceAnalysisDetailSpan) => {
  const start = spanStartMs(span)
  if (span.EndTime) {
    const end = dayjs(span.EndTime).valueOf()
    if (end > start) return end
  }
  return start + (span.DurationMs || 0)
}

const stepOf = (span: TraceAnalysisDetailSpan) => getLangGraphStep(span.Attributes)

const pickLatestFinishing = (candidates: TraceAnalysisDetailSpan[]) =>
  candidates.reduce((latest, candidate) =>
    spanEndMs(candidate) >= spanEndMs(latest) ? candidate : latest
  )

export function createLangGraphContext(spans: TraceAnalysisDetailSpan[]): LangGraphContext {
  const spanMap = new Map(spans.map(span => [span.SpanId, span]))
  const stepGroups = new Map<number, TraceAnalysisDetailSpan[]>()

  for (const span of spans) {
    const step = stepOf(span)
    if (step == null) continue
    const group = stepGroups.get(step) ?? []
    group.push(span)
    stepGroups.set(step, group)
  }

  const sortedSteps = Array.from(stepGroups.keys()).sort((a, b) => a - b)
  return {
    spans,
    spanMap,
    spanIds: new Set(spanMap.keys()),
    stepGroups,
    sortedSteps,
    minStep: sortedSteps[0] ?? null
  }
}

/**
 * step 级图节点：有 langgraph_step，且 OTEL 父 Span 无 step 或 step 不同。
 * 同 step 内嵌套子 Span（LLM generation、should_continue 等）为 graph_nested。
 */
function isStepLevelSpan(span: TraceAnalysisDetailSpan, ctx: LangGraphContext): boolean {
  const parentId = span.ParentSpanId
  if (!parentId || !ctx.spanIds.has(parentId)) return true
  const parent = ctx.spanMap.get(parentId)
  return !parent || stepOf(parent) !== stepOf(span)
}

function stepLevelSpans(ctx: LangGraphContext, step: number): TraceAnalysisDetailSpan[] {
  return (ctx.stepGroups.get(step) ?? []).filter(span => isStepLevelSpan(span, ctx))
}

export function splitStepSpans(ctx: LangGraphContext, step: number) {
  const stepSpans = ctx.stepGroups.get(step) ?? []
  const level = stepLevelSpans(ctx, step)
  const levelIds = new Set(level.map(span => span.SpanId))
  return {
    level,
    nested: stepSpans.filter(span => !levelIds.has(span.SpanId))
  }
}

function noStepSpans(ctx: LangGraphContext): TraceAnalysisDetailSpan[] {
  return ctx.spans.filter(span => stepOf(span) == null)
}

/** 无 langgraph_step 的 Span 按 OTEL 父子拆成多层（app_root → framework_root → …） */
export function getNoStepSpanLayers(ctx: LangGraphContext): TraceAnalysisDetailSpan[][] {
  const entries = noStepSpans(ctx)
  if (!entries.length) return []

  const entryIds = new Set(entries.map(span => span.SpanId))
  const childrenMap = new Map<string, TraceAnalysisDetailSpan[]>()

  for (const span of entries) {
    const parentId = span.ParentSpanId
    if (!parentId || !entryIds.has(parentId)) continue
    const list = childrenMap.get(parentId) ?? []
    list.push(span)
    childrenMap.set(parentId, list)
  }

  const roots = sortSpanSiblings(
    entries.filter(span => {
      const parentId = span.ParentSpanId
      return !parentId || !entryIds.has(parentId)
    }),
    false
  )

  const layers: TraceAnalysisDetailSpan[][] = []
  let current = roots
  while (current.length) {
    layers.push(current)
    const next: TraceAnalysisDetailSpan[] = []
    for (const span of current) {
      next.push(...sortSpanSiblings(childrenMap.get(span.SpanId) ?? [], false))
    }
    current = next
  }

  return layers
}

function createEdgeCollector() {
  const edges: LangGraphSpanEdge[] = []
  const keys = new Set<string>()
  const add = (from: string, to: string) => {
    const key = `${from}->${to}`
    if (keys.has(key)) return
    keys.add(key)
    edges.push({ from, to })
  }
  return { edges, add }
}

/** 无 step 入口链：app_root → framework_root（OTEL 父子） */
function addNoStepChainEdges(ctx: LangGraphContext, add: (from: string, to: string) => void) {
  for (const span of noStepSpans(ctx)) {
    const parentId = span.ParentSpanId
    if (!parentId || !ctx.spanIds.has(parentId)) continue
    const parent = ctx.spanMap.get(parentId)
    if (parent && stepOf(parent) == null) {
      add(parentId, span.SpanId)
    }
  }
}

/** framework / 无 step 链末端 → 首个 langgraph_step 图节点 */
function addFrameworkToFirstStepEdges(
  ctx: LangGraphContext,
  add: (from: string, to: string) => void
) {
  const { minStep } = ctx
  if (minStep == null) return

  for (const span of ctx.spans) {
    if (stepOf(span) !== minStep || !isStepLevelSpan(span, ctx)) continue

    const otelParentId = span.ParentSpanId
    if (otelParentId && ctx.spanIds.has(otelParentId)) {
      const otelParent = ctx.spanMap.get(otelParentId)
      if (otelParent && stepOf(otelParent) == null) {
        add(otelParentId, span.SpanId)
      }
    }
  }
}

/** 同 step 内 graph_step → graph_nested（OTEL 父子） */
function addSameStepNestedEdges(ctx: LangGraphContext, add: (from: string, to: string) => void) {
  for (const span of ctx.spans) {
    const spanStep = stepOf(span)
    if (spanStep == null) continue

    const parentId = span.ParentSpanId
    if (!parentId || !ctx.spanIds.has(parentId)) continue

    const parent = ctx.spanMap.get(parentId)
    if (!parent) continue

    if (stepOf(parent) === spanStep) {
      add(parentId, span.SpanId)
    }
  }
}

/** 相邻 step 间连边：优先 join/branch triggers，否则 step 级全连接 */
function addCrossStepEdges(ctx: LangGraphContext, add: (from: string, to: string) => void) {
  for (let i = 1; i < ctx.sortedSteps.length; i += 1) {
    const prevStep = ctx.sortedSteps[i - 1]
    const currStep = ctx.sortedSteps[i]
    const fromSpans = stepLevelSpans(ctx, prevStep)
    const toSpans = stepLevelSpans(ctx, currStep)

    // 是否已通过 join trigger 显式连边；未连上则退化为 step 间全连接
    let connected = false

    for (const to of toSpans) {
      const triggers = parseLangGraphTriggerItems(to.Attributes)
      for (const trigger of triggers) {
        if (trigger.type === 'join' && trigger.predecessorNodes?.length) {
          for (const predName of trigger.predecessorNodes) {
            const matchedFromSpans = fromSpans.filter(
              from => getLangGraphNode(from.Attributes) === predName || from.Name === predName
            )
            for (const from of matchedFromSpans) {
              add(from.SpanId, to.SpanId)
              connected = true
            }
          }
        }
      }
    }

    if (!connected) {
      fromSpans.forEach(from => toSpans.forEach(to => add(from.SpanId, to.SpanId)))
    }
  }
}

// --- 公开 API ---

export function isLangGraphTrace(spans: TraceAnalysisDetailSpan[]): boolean {
  return spans.some(span => {
    const attrs = span.Attributes
    return (
      stepOf(span) != null ||
      attrs?.[LS_INTEGRATION_ATTR] === 'langgraph' ||
      attrs?.[LANGCHAIN_ROOT_ATTR] === 'true' ||
      attrs?.[AS_ROOT_ATTR] === 'true'
    )
  })
}

/** 拓扑连边：属性分层 + OTEL 结构 + step 路由 */
function buildLangGraphEdgesFromContext(ctx: LangGraphContext): LangGraphSpanEdge[] {
  const { edges, add } = createEdgeCollector()

  addNoStepChainEdges(ctx, add)
  addFrameworkToFirstStepEdges(ctx, add)
  addCrossStepEdges(ctx, add)
  addSameStepNestedEdges(ctx, add)

  return edges
}

export function buildLangGraphEdges(spans: TraceAnalysisDetailSpan[]): LangGraphSpanEdge[] {
  return buildLangGraphEdgesFromContext(createLangGraphContext(spans))
}

/** 列表树单父：拓扑多父时取最近结束的上游 */
export function buildLangGraphParentMap(spans: TraceAnalysisDetailSpan[]): Map<string, string> {
  const parentMap = new Map<string, string>()
  if (!spans.length) return parentMap

  const ctx = createLangGraphContext(spans)
  const edges = buildLangGraphEdges(spans)
  const incoming = new Map<string, TraceAnalysisDetailSpan[]>()

  edges.forEach(edge => {
    const from = ctx.spanMap.get(edge.from)
    if (!from) return
    const list = incoming.get(edge.to) ?? []
    list.push(from)
    incoming.set(edge.to, list)
  })

  for (const span of spans) {
    const role = classifyLangfuseSpanRole(span, ctx)
    const step = stepOf(span)

    if (role === 'app_root') {
      parentMap.set(span.SpanId, LANGGRAPH_ROOT_KEY)
      continue
    }

    if (role === 'framework_root' || role === 'no_step') {
      const otelParentId = span.ParentSpanId
      if (
        otelParentId &&
        ctx.spanIds.has(otelParentId) &&
        stepOf(ctx.spanMap.get(otelParentId)!) == null
      ) {
        parentMap.set(span.SpanId, otelParentId)
      } else {
        parentMap.set(span.SpanId, LANGGRAPH_ROOT_KEY)
      }
      continue
    }

    if (role === 'graph_step' && step === ctx.minStep) {
      const otelParentId = span.ParentSpanId
      if (
        otelParentId &&
        ctx.spanIds.has(otelParentId) &&
        stepOf(ctx.spanMap.get(otelParentId)!) == null
      ) {
        parentMap.set(span.SpanId, otelParentId)
      } else {
        const frameworkRoot = noStepSpans(ctx).find(isFrameworkRootSpan)
        parentMap.set(span.SpanId, frameworkRoot?.SpanId ?? LANGGRAPH_ROOT_KEY)
      }
      continue
    }

    if (role === 'graph_step') {
      const parents = incoming.get(span.SpanId)
      parentMap.set(
        span.SpanId,
        parents?.length ? pickLatestFinishing(parents).SpanId : LANGGRAPH_ROOT_KEY
      )
      continue
    }

    // graph_nested：同 step OTEL 父节点
    const otelParentId = span.ParentSpanId
    if (otelParentId && ctx.spanIds.has(otelParentId)) {
      parentMap.set(span.SpanId, otelParentId)
    } else {
      const parents = incoming.get(span.SpanId)
      parentMap.set(
        span.SpanId,
        parents?.length ? pickLatestFinishing(parents).SpanId : LANGGRAPH_ROOT_KEY
      )
    }
  }

  return parentMap
}

function groupByParentKey(
  spans: TraceAnalysisDetailSpan[],
  parentMap: Map<string, string>
): Map<string, TraceAnalysisDetailSpan[]> {
  const map = new Map<string, TraceAnalysisDetailSpan[]>()
  for (const span of spans) {
    const key = parentMap.get(span.SpanId) ?? LANGGRAPH_ROOT_KEY
    const list = map.get(key) ?? []
    list.push(span)
    map.set(key, list)
  }
  return map
}

export function buildParentSpanChildrenMap(
  spans: TraceAnalysisDetailSpan[]
): Map<string, TraceAnalysisDetailSpan[]> {
  const ids = new Set(spans.map(span => span.SpanId))
  const parentMap = new Map<string, string>()
  for (const span of spans) {
    parentMap.set(
      span.SpanId,
      span.ParentSpanId && ids.has(span.ParentSpanId) ? span.ParentSpanId : LANGGRAPH_ROOT_KEY
    )
  }
  return groupByParentKey(spans, parentMap)
}

export function buildSpanChildrenMap(
  spans: TraceAnalysisDetailSpan[]
): Map<string, TraceAnalysisDetailSpan[]> {
  if (!isLangGraphTrace(spans)) {
    return buildParentSpanChildrenMap(spans)
  }
  return groupByParentKey(spans, buildLangGraphParentMap(spans))
}

export type LangGraphTableTreeRow = TraceAnalysisDetailSpan & {
  children?: LangGraphTableTreeRow[]
}

function toLangGraphTableRow(span: TraceAnalysisDetailSpan): LangGraphTableTreeRow {
  return { ...span }
}

function buildNoStepOtelTree(entries: TraceAnalysisDetailSpan[]): LangGraphTableTreeRow[] {
  if (!entries.length) return []

  const attach = (span: TraceAnalysisDetailSpan): LangGraphTableTreeRow => {
    const children = sortSpanSiblings(
      entries.filter(item => item.ParentSpanId === span.SpanId),
      false
    ).map(attach)
    return { ...span, children: children.length ? children : undefined }
  }

  const entryIds = new Set(entries.map(span => span.SpanId))
  const roots = sortSpanSiblings(
    entries.filter(span => {
      const parentId = span.ParentSpanId
      return !parentId || !entryIds.has(parentId)
    }),
    false
  )

  return roots.map(attach)
}

function graftStepRowsToLangChainRoot(
  rows: LangGraphTableTreeRow[],
  stepRows: LangGraphTableTreeRow[]
): LangGraphTableTreeRow[] {
  if (!stepRows.length) return rows

  return rows.map(row => {
    const children = row.children?.length
      ? graftStepRowsToLangChainRoot(row.children, stepRows)
      : undefined

    if (isFrameworkRootSpan(row)) {
      return {
        ...row,
        children: children?.length ? children : stepRows
      }
    }

    return { ...row, children }
  })
}

/**
 * Langfuse 表格树，与拓扑图一致的分层：
 * app_root → framework_root → graph_step(step) → graph_nested(LLM / 路由)。
 */
export function buildLangGraphTableTreeData(
  spans: TraceAnalysisDetailSpan[]
): LangGraphTableTreeRow[] {
  if (!spans.length) return []

  const ctx = createLangGraphContext(spans)
  const parentMap = buildLangGraphParentMap(spans)

  const entries = sortSpanSiblings(noStepSpans(ctx), false)
  const stepNodes = spans.filter(span => stepOf(span) != null && isStepLevelSpan(span, ctx))
  const nestedSpans = spans.filter(span => stepOf(span) != null && !isStepLevelSpan(span, ctx))

  const stepRows: LangGraphTableTreeRow[] = sortSpanSiblings(stepNodes, true).map(span => {
    const children = sortSpanSiblings(
      nestedSpans.filter(item => parentMap.get(item.SpanId) === span.SpanId),
      true
    ).map(toLangGraphTableRow)
    return { ...span, children: children.length ? children : undefined }
  })

  const noStepTree = buildNoStepOtelTree(entries)
  if (!noStepTree.length) {
    return stepRows
  }

  return graftStepRowsToLangChainRoot(noStepTree, stepRows)
}

export function collectLangGraphTableExpandableIds(rows: LangGraphTableTreeRow[]): string[] {
  const ids: string[] = []
  const walk = (list: LangGraphTableTreeRow[]) => {
    for (const row of list) {
      if (row.children?.length) {
        ids.push(row.SpanId)
        walk(row.children)
      }
    }
  }
  walk(rows)
  return ids
}

export function sortSpanSiblings(
  spans: TraceAnalysisDetailSpan[],
  langGraphMode: boolean
): TraceAnalysisDetailSpan[] {
  const list = [...spans]
  list.sort((a, b) => {
    if (langGraphMode) {
      const stepA = stepOf(a)
      const stepB = stepOf(b)
      if (stepA != null && stepB != null && stepA !== stepB) return stepA - stepB

      const seqA = getSeqStep(a.Attributes)
      const seqB = getSeqStep(b.Attributes)
      if (seqA != null && seqB != null && seqA !== seqB) return seqA - seqB
      if (seqA != null && seqB == null) return -1
      if (seqA == null && seqB != null) return 1
    }
    return spanStartMs(a) - spanStartMs(b)
  })
  return list
}

export {
  buildLangGraphSpanTopology as buildLangGraphTopology,
  buildLangGraphTopologyLayout,
  decideLangGraphLayoutDirection
} from './topology/langgraphSpanTopology'
export type {
  LangGraphSpanTopology as LangGraphTopology,
  LangGraphTopologyLayoutOptions
} from './topology/langgraphSpanTopology'
export type { TopologyLayoutDirection } from './topology/types'
